import { PromiseCompletionSource } from "scene-manager";
import { Event } from "event-system";
import { injectable } from "@launchtray/tsyringe-async";
import { IGameDistributionSdk } from "./interfaces/IGameDistributionSdk";
import { GameDistributionSdk } from "./data/GameDistributionSDK";
import { GameDistributionEventPayload } from "./data/GameDistributionEventPayload";
import { Language } from "../scenes/common/Language";
import { GameDistributionEventType } from "./data/GameDistributionEventType";

@injectable()
export class GameDistributionSdkWrapper implements IGameDistributionSdk {
    private static readonly GAME_ID = "8b7bf0af0fdf462dba1ce0152c158b38";

    public readonly pauseAudio: Event;
    public readonly resumeAudio: Event;

    private isAdPlaying: boolean = false;

    private sdkInstance: GameDistributionSdk;
    private initPCS: PromiseCompletionSource<void>;
    private readonly watchCompleteEvent: Event<GameDistributionEventPayload>;
    private readonly watchAllAdsCompletedEvent: Event<GameDistributionEventPayload>;

    public constructor() {
        this.onEvent = this.onEvent.bind(this);
        this.watchCompleteEvent = new Event();
        this.watchAllAdsCompletedEvent = new Event();
        this.pauseAudio = new Event();
        this.resumeAudio = new Event();
    }

    public async initialize(): Promise<void> {
        if (this.initPCS != null) {
            console.warn("Attempt to double init of GD sdk");
            return await this.initPCS.result;
        }
        this.initPCS = new PromiseCompletionSource();
        window.GD_OPTIONS = {
            gameId: GameDistributionSdkWrapper.GAME_ID,
            onEvent: this.onEvent,
            advertisementSettings: {
                debug: false,
                autoplay: false,
                locale: Language.English
            }
        };
        await this.loadSDKScript();
        await this.initPCS.result;
        this.sdkInstance = window.gdsdk;
        this.sdkInstance.preloadAd("rewarded");
        if (this.sdkInstance == null) {
            throw new Error("Game distribution sdk instance not found!");
        }
    }

    private async loadSDKScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://html5.api.gamedistribution.com/main.min.js";
            script.id = "gamedistribution-jssdk";
            script.crossOrigin = "anonymous";
            script.async = true;
            script.onload = () => resolve();
            script.onerror = (error) => reject(error);
            document.head.appendChild(script);
        });
    }

    private onEvent(event: GameDistributionEventPayload): void {
        switch (event.name) {
            case GameDistributionEventType.Ready: {
                // When the SDK is ready.
                this.initPCS.complete();
                break;
            }
            case GameDistributionEventType.Error: {
                // When the SDK has hit a critical error.
                this.initPCS.complete();
                break;
            }
            case GameDistributionEventType.GameStart: {
                // When the game should start.
                this.adGameStart();
                break;
            }
            case GameDistributionEventType.GamePause: {
                // When the game should pause.
                this.adGamePause();
                break;
            }
            case GameDistributionEventType.Tracking: {
                // When the publishers' client has requested to not track his/ her data. Hook into this event to find out if you can record client tracking data.
                break;
            }
            case GameDistributionEventType.Targeting: {
                // When the publishers' client has requested to not get personalized advertisements. Hook into this event to find out if you can display personalized advertisements in case you use another ad solution.
                break;
            }
            case GameDistributionEventType.RewardedWatchComplete: {
                // this event is triggered when your user completely watched rewarded ad
                this.watchCompleteEvent.emit(event);
                break;
            }
            case GameDistributionEventType.ContentResumeRequested: {
                // Fired when content should be resumed. This usually happens when an ad finishes or collapses.
                break;
            }
            case GameDistributionEventType.AllAdsCompleted: {
                this.watchAllAdsCompletedEvent.emit(event);
                break;
            }
            case GameDistributionEventType.AdSdkCanceled: {
                this.watchAllAdsCompletedEvent.emit(event);
                this.watchCompleteEvent.emit(event);
                break;
            }
            case GameDistributionEventType.AdError: {
                this.watchAllAdsCompletedEvent.emit(event);
                this.watchCompleteEvent.emit(event);
                break;
            }
        }
    }

    public showRewardedAd(): Promise<boolean> {
        this.isAdPlaying = true;
        return new Promise(async (resolve) => {
            if (typeof this.sdkInstance !== undefined && this.sdkInstance.showAd !== undefined) {
                const handleWatch = (event: GameDistributionEventPayload) => {
                    this.watchCompleteEvent.remove(handleWatch, this);
                    this.isAdPlaying = false;
                    if (event.status === "success") {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                };
                this.watchCompleteEvent.add(handleWatch, this);
                try {
                    const result = await this.sdkInstance.showAd("rewarded");
                    this.isAdPlaying = false;
                    if (result.args.success) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                } catch (error) {
                    console.error(error);
                    this.watchCompleteEvent.remove(handleWatch, this);
                    this.isAdPlaying = false;
                    resolve(false);
                }
            } else {
                this.isAdPlaying = false;
                resolve(false);
            }
        });
    }

    public async showMidrollAd(): Promise<void> {
        if (this.isAdPlaying) {
            return;
        }
        if (typeof this.sdkInstance !== undefined && this.sdkInstance.showAd !== undefined) {
            return new Promise(async (resolve) => {
                const handleWatch = (event: GameDistributionEventPayload) => {
                    this.watchAllAdsCompletedEvent.remove(handleWatch, this);
                    resolve();
                };
                this.watchAllAdsCompletedEvent.add(handleWatch, this);
                try {
                    const result = await this.sdkInstance.showAd("interstitial");
                } catch (error) {
                    console.error(error);
                    this.watchAllAdsCompletedEvent.remove(handleWatch, this);
                    resolve();
                }
            });
        }
    }

    private adGamePause(): void {
        this.pauseAudio.emit();
    }

    private adGameStart(): void {
        this.resumeAudio.emit();
    }

}
